import { renderDynamicApplyPage as renderBaseDynamicApplyPage } from './dynamicApply';
import { normalisePublishedPdfPlaceholders } from './dynamicApplyPlaceholders';

type Branding = {
  brand_cn?: string;
  brand_en?: string;
  document_title?: string;
  document_subtitle?: string;
  brand_cn_size?: number;
  brand_en_size?: number;
  title_size?: number;
  subtitle_size?: number;
  brand_cn_weight?: string;
  brand_en_weight?: string;
  title_weight?: string;
  subtitle_weight?: string;
  padding_top?: number;
  padding_bottom?: number;
  padding_x?: number;
  right_align?: 'left' | 'right';
  show_divider?: boolean;
};

type FormConfigEnvelope = {
  ok?: boolean;
  data?: {
    form?: {
      template_name?: string;
      schema?: { branding?: Branding };
    };
  };
};

function number(value: unknown, fallback: number, minimum: number, maximum: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(maximum, Math.max(minimum, parsed)) : fallback;
}

function text(root: HTMLElement, selector: string, value: string) {
  const element = root.querySelector<HTMLElement>(selector);
  if (element) element.textContent = value;
}

function style(root: HTMLElement, selector: string, values: Partial<CSSStyleDeclaration>) {
  const element = root.querySelector<HTMLElement>(selector);
  if (!element) return;
  Object.assign(element.style, values);
}

async function applyPublishedBranding(root: HTMLElement) {
  const form = root.querySelector<HTMLFormElement>('#applyForm');
  if (!form?.classList.contains('dynamic-application-paper-pdf')) return;

  const params = new URLSearchParams(location.search);
  const outletCode = (params.get('outlet') || 'KCH').toUpperCase();
  const response = await fetch(`/api/form-config?outlet=${encodeURIComponent(outletCode)}`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return;

  const envelope = await response.json() as FormConfigEnvelope;
  const templateName = String(envelope.data?.form?.template_name || 'Outlet Job Application');
  const branding = envelope.data?.form?.schema?.branding || {};
  const brandCn = String(branding.brand_cn ?? '阿笨的猪堡店');
  const brandEn = String(branding.brand_en ?? "STUPIAK'S PORK BURGER");
  const documentTitle = String(branding.document_title ?? 'Job Application');
  const documentSubtitle = String(branding.document_subtitle || templateName);

  text(root, '.dynamic-pdf-masthead .brand-cn', brandCn);
  text(root, '.dynamic-pdf-masthead .brand-en', brandEn);
  text(root, '.dynamic-pdf-masthead .form-title', documentTitle);
  text(root, '.dynamic-pdf-masthead .form-subtitle', documentSubtitle);

  style(root, '.dynamic-pdf-masthead .brand-cn', {
    fontSize: `${number(branding.brand_cn_size, 27, 8, 64)}px`,
    fontWeight: String(branding.brand_cn_weight || '900'),
  });
  style(root, '.dynamic-pdf-masthead .brand-en', {
    fontSize: `${number(branding.brand_en_size, 15, 8, 64)}px`,
    fontWeight: String(branding.brand_en_weight || '900'),
  });
  style(root, '.dynamic-pdf-masthead .form-title', {
    fontSize: `${number(branding.title_size, 28, 8, 64)}px`,
    fontWeight: String(branding.title_weight || '500'),
  });
  style(root, '.dynamic-pdf-masthead .form-subtitle', {
    fontSize: `${number(branding.subtitle_size, 10, 8, 48)}px`,
    fontWeight: String(branding.subtitle_weight || '700'),
  });
  style(root, '.dynamic-pdf-masthead .title-lockup', {
    textAlign: branding.right_align === 'left' ? 'left' : 'right',
  });
  style(root, '.dynamic-pdf-masthead', {
    padding: `${number(branding.padding_top, 2, 0, 48)}px ${number(branding.padding_x, 4, 0, 64)}px ${number(branding.padding_bottom, 8, 0, 48)}px`,
    borderBottom: branding.show_divider ? '1px solid #111' : '0 solid transparent',
  });
}

export async function renderDynamicApplyPage(root: HTMLElement): Promise<void> {
  await renderBaseDynamicApplyPage(root);
  normalisePublishedPdfPlaceholders(root);
  try {
    await applyPublishedBranding(root);
  } catch {
    // Branding is optional. The form remains usable with its default masthead.
  }
}
